import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { checkNonMoneyDataForTable, trimObject } from "utils/utils";
import s from "./AgentsDetail.module.scss";
import { queryAgentMerchant } from "./model";
import { searchErrorHandler } from "utils/formValidation";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import ColumnFilter from "components/ColumnFilter/ColumnFilter";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";

import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import merchantManagement from "routes/merchantManagement";

const PAGE_LOCALE_KEY = "page.agents.agents_detail.";

const SEARCH_FIELD_INIT_STATE = {
  isoAgentId: "",
  firstname: "",
  lastname: "",
  email: "",
};

const ERROR_INIT_STATE = {
  firstName: false,
  lastName: false,
  email: false,
  role: false,
  isOTPenable: false,
  timezone: false,
  permission: false,
  status: false,
};

const AgentsDetailMerchants = ({ IsoAgentId, IsoAgentEmail, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "AgentMerchantsListGrid";

  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const isInitRef = useRef(true);
  const [datasource, setDatasource] = useState({
    merchants: { data: [] },
    agentDetails: { data: [] },
  });

  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    SEARCH_FIELD_INIT_STATE,
  );

  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);

  const [columnDisplayDialog, setColumnDisplayDialog] = useState({
    open: false,
    position: 0,
  });
  const [columnsArray, setColumnsArray] = useState([]);

  useLayoutEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    if (!IsoAgentId) {
      //message "unknown payment id" will be shown
      setLoading(false);
      return;
    }

    initialFetchList(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, [IsoAgentId]);

  const initialFetchList = async (getIsOudated) => {
    setLoading(true);
    const apiResponse = await queryAgentMerchant(
      trimObject({
        page: pageSizeInfoRef.current.currentpage,
        size: pageSizeInfoRef.current.pagesize,
        isoAgentId: IsoAgentId,
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            isInitRef.current = false;
            setDatasource((state) => ({ ...state, merchants: responseBody }));
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDatasource((state) => ({
              ...state,
              merchants: {
                total: 0,
                page: 0,
                size: 10,
                data: [],
              },
            }));
          }
        }
      }
    }
    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput(SEARCH_FIELD_INIT_STATE);
  };

  const resetError = () => {
    setError(ERROR_INIT_STATE);
  };

  const handleReset = () => {
    isInitRef.current = true;
    setIsIdInput(false);
    resetInputField();
    resetError();
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, []);
      setError(errorObj);
      if (hasError) return;

      resetError();
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      isoAgentId: IsoAgentId,
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryAgentMerchant(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            isInitRef.current = false;
            setDatasource((state) => ({ ...state, merchants: responseBody }));
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDatasource((state) => ({
              ...state,
              merchants: {
                total: 0,
                page: 0,
                size: 10,
                data: [],
              },
            }));
          }
        }
      }
    }

    setSearch(true);
    setSearching(false);
  };

  // ===================== search field function start ===================== //

  const searchInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };

  const searchInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const searchInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  // ===================== search field function end ===================== //
  // ===================== column display function ===================== //

  const openColumnDisplay = (e) => {
    const position = e.target.getBoundingClientRect().top;
    setColumnDisplayDialog({
      open: true,
      position: position,
    });
    return;
  };

  const closeColumnDisplay = () => {
    setColumnDisplayDialog({
      ...columnDisplayDialog,
      open: false,
    });
    return;
  };

  // ===================== column display function ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const columns = [
    {
      key: "merchantId",
      title: t(PAGE_LOCALE_KEY + "columnTitle.isoAgentId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "name",
      title: t(PAGE_LOCALE_KEY + "columnTitle.lastname"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "email",
      title: t(PAGE_LOCALE_KEY + "columnTitle.email"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },

    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "action",
      title: t(PAGE_LOCALE_KEY + "columnTitle.action"),
      sorter: false,
      render: (data) => {
        const { id } = data;

        return (
          <div className={s.actionBtnContainer}>
            <button
              onClick={() => {
                const path =
                  "/merchant-management/merchants/add-offline-merchant";
                const payload = {
                  name: merchantManagement
                    .find((tab) => tab.tabName === "Merchants")
                    .page.find((page) => page.path === path).name,
                  path: path,
                  state: {
                    id: id,
                    isoAgentEmail: IsoAgentEmail,
                  },
                };
                redirectFn("redirectPage", payload);
              }}
            >
              {t(PAGE_LOCALE_KEY + "columnTitle.action3")}
            </button>
          </div>
        );
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.isoAgentId.label")}
            value={searchFieldInput.isoAgentId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => searchInputOnChange(e, "isoAgentId")}
            onfocusFn={() => searchInputOnFocus("isoAgentId")}
            onblurFn={() => searchInputOnBlur("isoAgentId")}
            sxParams={{ textFocus: focus.isoAgentId }}
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.email.label")}
            value={searchFieldInput.email}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => searchInputOnChange(e, "email")}
            onfocusFn={() => searchInputOnFocus("email")}
            onblurFn={() => searchInputOnBlur("email")}
            sxParams={{ textFocus: focus.email }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading}
          filterStatus={columnDisplayDialog}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          handleColumnDisplay={openColumnDisplay}
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "agent-detail-merchants",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.merchants.data}
              total={datasource.merchants.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}
          <ColumnFilter
            columnDisplayDialog={columnDisplayDialog}
            dialogOncloseFn={closeColumnDisplay}
            columns={columns}
            columnsArray={columnsArray}
            rememberPreference="agent-detail-merchants"
            setColumnsArray={setColumnsArray}
          />
          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(AgentsDetailMerchants);
